import {
  useEffect,
  useState,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import Slider from "react-slick";
import { useLazyQuery } from "@apollo/client";
import { useImmer } from "use-immer";

import "react-datepicker/dist/react-datepicker.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import {
  previousMonthRangeSelect,
  sortFunction,
  inputDateFormat,
} from "../../../service/utils/functions";
import { DataType, SortOrder } from "../../../service/utils/constants";
import { useDarkModeCheck } from "../DashboardLayout";
import {
  GET_CUSTOMERS,
  GET_SUGGESTIONS,
  GET_HOME_DATA,
  GET_EVENTBRITE_DATA,
  GET_HOME_DATA_TIPPING,
} from "../../../service/graphql/query";
import * as Storage from "../../../service/storage";
import {
  useCustomerFilters,
  useRequestGuard,
} from "../../../service/hooks/customerData";
import {
  CustomerDataEntry,
  GenericGraphDataEntry,
  OverallRecycledWasteEntry,
  WasteTotalVerticalEntry,
} from "../../../service/utils/interfaces";
import {
  kpiFormat,
  wasteTypeChartFormat,
  recycledRecoveredChartFormat,
  emissionsChartFormat,
  wasteTypeByPeriodChartFormat,
  wasteTypeBySiteChartFormat,
  topSitesByRecyclingRateFormat,
  bottomSitesByEmissionsRateFormat,
  topSitesByReducedWasteRateFormat,
  printableWasteTypeChartFormat,
  parseLevelData,
  parseTonnageData,
  parseFeeData,
  parseAchievementTippingData,
} from "../../../service/utils/parsers";
import { exportDownload } from "./export-components/functions";
import HomePageUI from "../../../ui/dashboard/home/HomePageUI";

const animationDuration = 2000; // animation duration in milliseconds

const HomePage = () => {
  const { darkMode } = useDarkModeCheck();

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() =>
    previousMonthRangeSelect()
  );
  const [startDate, endDate] = dateRange;
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [isTippingUser, setIsTippingUser] = useState<boolean>(false);
  const [customerList, setCustomerList] = useState<CustomerDataEntry[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerDataEntry>();
  const [wasteTypesPerCustomer, setWasteTypePerCustomer] = useState<any[]>([]);
  const [selectedWasteTypes, setSelectedWasteTypes] = useState<any>();
  const [sitesPerCustomer, setSitesPerCustomer] = useState<any[]>([]);
  const [selectedSites, setSelectedSites] = useState<any>();
  const [achievementDataArray, setAchievementDataArray] = useState<any[]>([]);
  const [suggestionsArray, setSuggestionsArray] = useState<any[]>([]);

  const [overallRecycledWastes, setOverallRecycledWaste] = useState<
    OverallRecycledWasteEntry[]
  >([]);
  const [overallRecycledWastesExportTable, setOverallRecycledWasteExportTable] =
    useState<any[]>([]);
  const [overallRecycledWastesColors, setOverallRecycledWastesColors] =
    useState<any[]>([]);
  const [recycledWasteTypes, setRecycledWasteTypes] = useState<
    GenericGraphDataEntry[]
  >([]);
  const [printRecycledWasteTypes, setPrintRecycledWasteTypes] = useState<
    GenericGraphDataEntry[]
  >([]);
  const [recycledWasteDetails, setRecycledWasteDetails] = useState<any[][]>([]);
  const [printRecycledWasteDetails, setPrintRecycledWasteDetails] = useState<
    any[][]
  >([]);
  const [colorArray, setColorArray] = useState<any[]>([]);
  const [totalWasteCount, setTotalWasteCount] = useState<number>(0);
  const [emissionsGeneratedDetails, setEmissionsGeneratedDetails] = useState<
    GenericGraphDataEntry[]
  >([]);
  const [filterLeaderboardValue, setFilterLeaderboardValue] =
    useState<number>(1);
  const [sortingLeaderboardValue, setSortingLeaderboardValue] =
    useState<SortOrder>(SortOrder.Descending);
  const [leaderboardRecycling, setLeaderboardRecycling] = useImmer<{top: WasteTotalVerticalEntry[], bottom: WasteTotalVerticalEntry[]}>({top: [], bottom: []});
  const [leaderboardEmissions, setLeaderboardEmissions] = useImmer<{top: WasteTotalVerticalEntry[], bottom: WasteTotalVerticalEntry[]}>({top: [], bottom: []});
  const [leaderboardWaste, setLeaderboardWaste] = useImmer<{top: WasteTotalVerticalEntry[], bottom: WasteTotalVerticalEntry[]}>({top: [], bottom: []});
  const [supportUsername, setSupportUsername] = useState<string>("");
  const [supportEmail, setSupportEmail] = useState<string>("");
  const [eventbriteTitle, setEventbriteTitle] = useState<string>("");
  const [eventbriteBanner, setEventbriteBanner] = useState<string>("");
  const [eventbriteEventLink, setEventbriteEventLink] = useState<string>("");

  const [WTByPeriodGraph, setWTByPeriodGraph] = useState<any[]>([]);
  const [WTByPeriodTable, setWTByPeriodTable] = useState<any[]>([]);
  const [WTByPeriodLegend, setWTByPeriodLegend] = useState<any[]>([]);
  const [WTBySiteGraph, setWTBySiteGraph] = useState<any[]>([]);
  const [WTBySiteAdditionalRows, setWTBySiteAdditionalRows] =
    useState<number>(0);
  const [WTBySiteTable, setWTBySiteTable] = useState<any[]>([]);
  const [WTBySiteLegend, setWTBySiteLegend] = useState<any[]>([]);
  const [LBRecyclingRateTopGraph, setLBRecyclingRateTopGraph] = useState<any[]>(
    []
  );
  const [LBRecyclingRateTopTable, setLBRecyclingRateTopTable] = useState<any[]>(
    []
  );
  const [LBEmissionRateBottomGraph, setLBEmissionsRateBottomGraph] = useState<
    any[]
  >([]);
  const [LBEmissionRateBottomTable, setLBEmissionsRateBottomTable] = useState<
    any[]
  >([]);
  const [LBReducedWasteRateTopGraph, setLBReducedWasteRateTopGraph] = useState<
    any[]
  >([]);
  const [LBReducedWasteRateTopTable, setLBReducedWasteRateTopTable] = useState<
    any[]
  >([]);

  const [achievementsTipping, setAchievementsTipping] = useState<any[]>([]);
  const [feeDataTable, setFeeDataTable] = useState<any[]>([]);
  const [tonnageDataTable, setTonnageDataTable] = useState<any[]>([]);
  const [levelDataTable, setLevelDataTable] = useState<any[]>([]);

  const [getSuggestionList, { data: suggestionsData }] =
    useLazyQuery(GET_SUGGESTIONS);
  const [getCustomers, { data: customersData }] = useLazyQuery(GET_CUSTOMERS);
  const loadCustomerFilters = useCustomerFilters();
  const isNewHomeRequest = useRequestGuard();
  // Customer whose sites and waste types are loaded into the filter state.
  // Home data waits for it, so no call goes out with the previous customer's sites.
  const [filtersCustomerId, setFiltersCustomerId] = useState<number>();
  const [isDateRangeReady, setIsDateRangeReady] = useState(false);
  const [getHomeData, { data: homeData }] = useLazyQuery(GET_HOME_DATA);
  const [getHomeDataTipping, { data: homeDataTipping }] = useLazyQuery(
    GET_HOME_DATA_TIPPING
  );
  const [getEventbriteData, { data: eventbriteData }] =
    useLazyQuery(GET_EVENTBRITE_DATA);

  const [width, setWidth] = useState<number>(0);
  const [exportInProgress, setExportInProgress] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    setWidth(window.innerWidth);
  }, []);

  const handleWindowResize = useCallback(() => {
    setWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    Storage.FILTER_DATA.get().then((filter) => {
      const start =
        filter.startDate.length > 0 ? new Date(filter.startDate) : null;
      const end = filter.endDate.length > 0 ? new Date(filter.endDate) : null;
      if (start && end) setDateRange([start, end]);
      else {
        setDateRange(previousMonthRangeSelect());
      }
      setIsDateRangeReady(true);
    });

    // Correctly load the logged-in user's profile from its dedicated storage.
    // This fixes the support form.
    Storage.PROFILE_DATA.get().then((profile) => {
      if (profile) {
        setSupportUsername(profile.name);
        setSupportEmail(profile.email);
      }
    });

    Storage.USER_DATA.get()
      .then(async (customerList) => {
        if (customerList && customerList.length > 0) {
          setIsTippingUser(customerList[0].category === "Tipping");
          setCustomerList(customerList);
          const filter = await Storage.FILTER_DATA.get();
          const profile = await Storage.PROFILE_DATA.get();
          const idx = filter.customerIndex >= 0 ? filter.customerIndex : 0;
          setSelectedCustomer(customerList[idx]);
        } else getCustomers({ context: { apiName: "middleware" } });
      })
      .catch(() => {
        getCustomers({ context: { apiName: "middleware" } });
      });
    getSuggestionList({ context: { apiName: "cms" } });
    getEventbriteData({ context: { apiName: "cms" } });

    // Recalculation handler for charts width
    window.addEventListener("resize", handleWindowResize);

    return () => window.removeEventListener("resize", handleWindowResize);
  }, [getSuggestionList, getEventbriteData, getCustomers, handleWindowResize]);

  useEffect(() => {
    if (!selectedCustomer) return;
    let isCurrent = true;

    (async () => {
      // Tipping customers have no site or waste type filters.
      const customer: CustomerDataEntry =
        selectedCustomer.category === "Tipping"
          ? { ...selectedCustomer, sites: [], wastes: [] }
          : await loadCustomerFilters(selectedCustomer);
      const filter = await Storage.FILTER_DATA.get();
      if (!isCurrent) return;

      const sites = customer.sites ?? [];
      const wastes = customer.wastes ?? [];
      setSitesPerCustomer(sites);
      setWasteTypePerCustomer(wastes);
      setSelectedSites(
        sites.filter((_, idx) => filter.siteIndex?.includes(idx))
      );
      setSelectedWasteTypes(
        wastes.filter((_, idx) => filter.wasteTypeIndex?.includes(idx))
      );
      setFiltersCustomerId(selectedCustomer.value);
    })();

    return () => {
      isCurrent = false;
    };
  }, [selectedCustomer, loadCustomerFilters]);

  useEffect(() => {
    if (
      isDateRangeReady &&
      selectedCustomer &&
      filtersCustomerId === selectedCustomer.value &&
      selectedSites &&
      selectedWasteTypes &&
      dateRange &&
      dateRange[0] &&
      dateRange[1]
    ) {
      let siteIdList: any[] = [];
      if (selectedSites.length > 0)
        siteIdList = selectedSites.map((el: any) => el.value);
      else siteIdList = sitesPerCustomer.map((el: any) => el.value);

      let wasteTypeIdList: any[] = [];
      if (selectedWasteTypes.length > 0)
        wasteTypeIdList = selectedWasteTypes.map((el: any) => el.value);
      else wasteTypeIdList = wasteTypesPerCustomer.map((el: any) => el.value);

      const input = isTippingUser
        ? {
            documentId: selectedCustomer?.value,
            startDate: inputDateFormat(dateRange[0] as Date),
            endDate: inputDateFormat(dateRange[1] as Date),
          }
        : {
            siteList: siteIdList,
            wasteList: wasteTypeIdList,
            startDate: inputDateFormat(dateRange[0] as Date),
            endDate: inputDateFormat(dateRange[1] as Date),
          };
      // Skip the call when the effect re-runs with the same filters, e.g. when
      // the filter row re-sends an unchanged date range.
      if (!isNewHomeRequest({ isTippingUser, input })) return;

      isTippingUser
        ? getHomeDataTipping({
            variables: { input },
            context: { apiName: "middleware" },
          })
        : getHomeData({
            variables: { input },
            context: { apiName: "middleware" },
          });
    }
  }, [
    selectedCustomer,
    filtersCustomerId,
    isDateRangeReady,
    isNewHomeRequest,
    selectedSites,
    selectedWasteTypes,
    sitesPerCustomer,
    wasteTypesPerCustomer,
    dateRange,
    isTippingUser,
    getHomeDataTipping,
    getHomeData,
  ]);

  useEffect(() => {
    if (
      suggestionsData &&
      suggestionsData.getRandomizedSuggestions &&
      suggestionsData.getRandomizedSuggestions.data
    ) {
      const resList = suggestionsData.getRandomizedSuggestions.data.map(
        (elem: any) => {
          const content = elem.attributes;
          return {
            image: content.image.data?.attributes?.url,
            title: content.title,
            description: content.description,
            linkUrl: content.linkUrl,
          };
        }
      );
      setSuggestionsArray(resList);
    }
  }, [suggestionsData]);

  useEffect(() => {
    if (
      eventbriteData &&
      eventbriteData.eventBanners &&
      eventbriteData.eventBanners.data &&
      eventbriteData.eventBanners.data.length > 0
    ) {
      const eventData = eventbriteData.eventBanners.data[0].attributes;
      if (eventData) {
        setEventbriteTitle(eventData.title);
        setEventbriteBanner(eventData.image.data.attributes.url);
        setEventbriteEventLink(eventData.link);
      }
    }
  }, [eventbriteData]);

  const manageCustomersData = useCallback(async (customersData: any) => {
    const tempCustomerList: CustomerDataEntry[] = [];
    if (
      customersData.getCustomers.length > 0 &&
      customersData.getCustomers[0].category === "Tipping"
    )
      setIsTippingUser(true);
    customersData.getCustomers.forEach((elem: any, idx: number) => {
      if (idx === 0) {
        setSupportUsername(elem.contactName);
        setSupportEmail(elem.email);
        Storage.PROFILE_DATA.set({ name: elem.contactName, email: elem.email });
      }
      // Sites and waste types are loaded later, for the selected customer only.
      tempCustomerList.push({
        value: elem.id,
        label: elem.customer,
        category: elem.category,
      });
    });
    setCustomerList(tempCustomerList);
    const filter = await Storage.FILTER_DATA.get();
    const idx = filter.customerIndex >= 0 ? filter.customerIndex : 0;
    setSelectedCustomer(tempCustomerList[idx]);
    Storage.USER_DATA.set(tempCustomerList);
  }, []);

  useEffect(() => {
    if (customersData && customersData.getCustomers) {
      manageCustomersData(customersData);
    }
  }, [customersData, manageCustomersData]);

  const manageHomeData = useCallback(
    (homeData: any) => {
      const dashboardArray: any[] = [];
      homeData.getHomeData.dashboard.forEach((arr: any[]) => {
        arr
          .filter((el) => el.dataset !== null)
          .forEach((item) => {
            dashboardArray.push({
              customerSiteLabel: item.customerSiteLabel,
              dataset: item.dataset,
              totalWeightCY: item.totalWeightCY ? item.totalWeightCY : 0,
              totalWeightYA: item.totalWeightYA ? item.totalWeightYA : 0,
              reducedWaste: item.reducedWaste ? item.reducedWaste : 0,
              reducedWasteRate: item.reducedWasteRate
                ? item.reducedWasteRate
                : 0,
              co2EmissionsGenerated: item.co2EmissionsGenerated
                ? item.co2EmissionsGenerated
                : 0,
              emissionsRate: item.emissionsRate ? item.emissionsRate : 0,
              percentage: item.percentage ? item.percentage : 0,
              periodLabel: item.periodLabel,
              periodValue: item.periodValue,
              recycledOrRecovered: item.recycledOrRecovered,
              recyclingRate: item.recyclingRate ? item.recyclingRate : 0,
              rorColorCode: item.rorColorCode,
              typeColorCode: item.typeColorCode,
              totalWeight: item.totalWeight ? item.totalWeight : 0,
              wasteTypeLabel: item.wasteTypeLabel,
              totalEmissions: item.totalEmissions ? item.totalEmissions : 0,
              totalRecycled: item.totalRecycled ? item.totalRecycled : 0,
              totalWaste: item.totalWaste ? item.totalWaste : 0,
            });
          });
      });

      // Populate KPIs
      const kpiResult = homeData.getHomeData.dahsboardKPI;
      const updatedAchievementArray = kpiFormat(kpiResult);
      setAchievementDataArray(updatedAchievementArray);

      // Populate WTChart
      const parsedWTChart = wasteTypeChartFormat(dashboardArray);
      setRecycledWasteDetails(parsedWTChart.tableData);
      setColorArray(parsedWTChart.colorArray);
      setTotalWasteCount(parsedWTChart.totalCount);
      setRecycledWasteTypes(parsedWTChart.pieData);

      // Populate WTReportTable
      const parsedWTReportTable = printableWasteTypeChartFormat(dashboardArray);
      setPrintRecycledWasteDetails(parsedWTReportTable.tableData);
      setPrintRecycledWasteTypes(parsedWTReportTable.pieData);

      // Populate RRChart
      const parsedRRChart = recycledRecoveredChartFormat(dashboardArray);
      setOverallRecycledWaste(parsedRRChart.data);
      setOverallRecycledWasteExportTable(parsedRRChart.table);
      setOverallRecycledWastesColors(parsedRRChart.colors);

      // Populate EmissionsChart
      const parsedEmissionsChart = emissionsChartFormat(dashboardArray);
      setEmissionsGeneratedDetails(parsedEmissionsChart);

      // Prepare WTChartByPeriod
      const parsedWTChartByPeriod =
        wasteTypeByPeriodChartFormat(dashboardArray);
      setWTByPeriodGraph(parsedWTChartByPeriod.graphArray);
      setWTByPeriodTable(parsedWTChartByPeriod.tableArray);
      setWTByPeriodLegend(parsedWTChartByPeriod.typeArray);

      // Prepare WTChartBySite
      const parsedWTChartBySite = wasteTypeBySiteChartFormat(dashboardArray);
      setWTBySiteGraph(parsedWTChartBySite.graphArray);
      setWTBySiteTable(parsedWTChartBySite.tableArray);
      setWTBySiteLegend(parsedWTChartBySite.typeArray);
      const additionalPages = Math.floor(
        parsedWTChartBySite.graphArray.length / 16
      );
      setWTBySiteAdditionalRows(additionalPages);

      // Prepare Leaderboard Graphs for export
      const parsedLBRecyclingRateTop =
        topSitesByRecyclingRateFormat(dashboardArray);
      setLBRecyclingRateTopGraph(parsedLBRecyclingRateTop.graphArray);
      setLBRecyclingRateTopTable(parsedLBRecyclingRateTop.tableArray);

      const parsedLBEmissionsRateBottom =
        bottomSitesByEmissionsRateFormat(dashboardArray);
      setLBEmissionsRateBottomGraph(parsedLBEmissionsRateBottom.graphArray);
      setLBEmissionsRateBottomTable(parsedLBEmissionsRateBottom.tableArray);

      const parsedLBReducedWasteRateTop =
        topSitesByReducedWasteRateFormat(dashboardArray);
      setLBReducedWasteRateTopGraph(parsedLBReducedWasteRateTop.graphArray);
      setLBReducedWasteRateTopTable(parsedLBReducedWasteRateTop.tableArray);

      // BUILD SEPARATE TOP AND BOTTOM LEADERBOARD ARRAYS
      const LB_RecyclingRateTop = homeData.getHomeData.dashboard[5] || [];
      const LB_RecyclingRateBottom = homeData.getHomeData.dashboard[6] || [];
      const LB_EmissionsRateTop = homeData.getHomeData.dashboard[7] || [];
      const LB_EmissionsRateBottom = homeData.getHomeData.dashboard[8] || [];
      const LB_ReducedWasteTop = homeData.getHomeData.dashboard[9] || [];
      const LB_ReducedWasteBottom = homeData.getHomeData.dashboard[10] || [];

      // Create arrays for TOP performers (initial view)
      const recyclingTopData: WasteTotalVerticalEntry[] = LB_RecyclingRateTop.map((item: any) => ({
        name: (item?.customerSiteLabel || "").replace(/&/g, "and"),
        value1: item?.recyclingRate ? parseFloat((item.recyclingRate * 100).toFixed(1)) : 0,
        value2: 0,
        value3: 0,
      }));

      const emissionsTopData: WasteTotalVerticalEntry[] = LB_EmissionsRateTop.map((item: any) => ({
        name: (item?.customerSiteLabel || "").replace(/&/g, "and"),
        value1: 0,
        value2: item?.emissionsRate ? parseFloat((item.emissionsRate * 100).toFixed(1)) : 0,
        value3: 0,
      }));

      const wasteTopData: WasteTotalVerticalEntry[] = LB_ReducedWasteTop.map((item: any) => ({
        name: (item?.customerSiteLabel || "").replace(/&/g, "and"),
        value1: 0,
        value2: 0,
        value3: item?.reducedWasteRate ? parseFloat((item.reducedWasteRate * 100).toFixed(1)) : 0,
      }));

      // Create arrays for BOTTOM performers (toggled view)
      const recyclingBottomData: WasteTotalVerticalEntry[] = LB_RecyclingRateBottom.map((item: any) => ({
        name: (item?.customerSiteLabel || "").replace(/&/g, "and"),
        value1: item?.recyclingRate ? parseFloat((item.recyclingRate * 100).toFixed(1)) : 0,
        value2: 0,
        value3: 0,
      }));

      const emissionsBottomData: WasteTotalVerticalEntry[] = LB_EmissionsRateBottom.map((item: any) => ({
        name: (item?.customerSiteLabel || "").replace(/&/g, "and"),
        value1: 0,
        value2: item?.emissionsRate ? parseFloat((item.emissionsRate * 100).toFixed(1)) : 0,
        value3: 0,
      }));

      const wasteBottomData: WasteTotalVerticalEntry[] = LB_ReducedWasteBottom.map((item: any) => ({
        name: (item?.customerSiteLabel || "").replace(/&/g, "and"),
        value1: 0,
        value2: 0,
        value3: item?.reducedWasteRate ? parseFloat((item.reducedWasteRate * 100).toFixed(1)) : 0,
      }));

      // Combine TOP and BOTTOM arrays for each leaderboard type
      const recyclingCombinedData = { top: recyclingTopData, bottom: recyclingBottomData };
      const emissionsCombinedData = { top: emissionsTopData, bottom: emissionsBottomData };
      const wasteCombinedData = { top: wasteTopData, bottom: wasteBottomData };

      setLeaderboardRecycling(recyclingCombinedData);
      setLeaderboardEmissions(emissionsCombinedData);
      setLeaderboardWaste(wasteCombinedData);

      setIsLoading(false);
    },
    [setLeaderboardRecycling, setLeaderboardEmissions, setLeaderboardWaste]
  );

  useEffect(() => {
    if (homeData && homeData.getHomeData) {
      manageHomeData(homeData);
    }
  }, [homeData, manageHomeData]);

  useEffect(() => {
    if (homeDataTipping && homeDataTipping.getHomeTippingData) {
      const dashboardTipping =
        homeDataTipping.getHomeTippingData.dashboardTipping;
      const achievementRow = dashboardTipping.achievementRow;
      const feeData = dashboardTipping.feeData;
      const tonnageData = dashboardTipping.tonnageData;
      const levelData = dashboardTipping.levelData;

      // Set achievement data
      const parsedAchievementData = parseAchievementTippingData(achievementRow);
      setAchievementsTipping(parsedAchievementData);

      // Set fee data
      const parsedFeeData = parseFeeData(feeData);
      setFeeDataTable(parsedFeeData);

      // Set tonnage data
      const parsedTonnageData = parseTonnageData(tonnageData);
      setTonnageDataTable(parsedTonnageData);

      // Set level data
      const parsedLevelData = parseLevelData(levelData);
      setLevelDataTable(parsedLevelData);

      setIsLoading(false);
    }
  }, [homeDataTipping]);

  const sliderRef = useRef<Slider | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0);

  useEffect(() => {
    if (exportInProgress) {
      const timer = setTimeout(async () => {
        await exportDownload(WTBySiteAdditionalRows);
        setExportInProgress(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [exportInProgress, WTBySiteAdditionalRows]);

  // Carbone export payload builder
    const getCarboneExportPayload = () => {
    const getLabels = (arr: any[]) =>
        Array.isArray(arr) ? arr.map((e) => e?.label || e?.name || "").filter(Boolean) : [];

    const getIds = (arr: any[]) =>
        Array.isArray(arr) ? arr.map((e) => e?.value || e?.id).filter(Boolean) : [];


    const chartCommonFontSize = 26;

    // DONUT CHART FOR WASTE TYPE PERCENTAGE - START:
    const dashboard2Array = homeData.getHomeData.dashboard && homeData.getHomeData.dashboard[2] ? homeData.getHomeData.dashboard[2] : [];

    const totalWeight = dashboard2Array.reduce(
  (sum: number, entry: { totalWeight?: number }) => sum + (entry.totalWeight || 0),
  0
);

    const pieData = dashboard2Array.map(
  (entry: { percentage: number; wasteTypeLabel: string; typeColorCode: string; totalWeight: number }) => ({
    value: +(entry.percentage * 100).toFixed(2),
    name: entry.wasteTypeLabel + ' (' + (entry.percentage * 100).toFixed(2) + "%, " + entry.totalWeight.toFixed(2) + "t)",
    itemStyle: { color: entry.typeColorCode, borderColor: "#ffffff", borderWidth: 1 },
  })
);


    const wastetypepercent = {
    type: "echarts@v5a",
    width: 800,
    height: 800,
    option: {
        tooltip: { trigger: "item" },
        legend: {
            top: "62%",
            left: "center",
            itemGap: 15,
            orient: "vertical",
            textStyle: { fontSize: chartCommonFontSize },
            show: false
        },
        graphic: [
        {
            type: "text",
            left: "center",
            top: "center",
            style: {
            text: `${totalWeight.toFixed(3)}t\nTotal Waste`,
            textAlign: "center",
            fill: "#000",
            fontSize: (chartCommonFontSize+10),
            fontWeight: "bold"
            }
        }
        ],
        series: [
        {
            name: "Access From",
            type: "pie",
            radius: ["35%", "60%"],
            label: { show: false, color: "grey", position: "outside", width: 200, overflow: 'break', fontSize: chartCommonFontSize },
            data: pieData
        }
        ]
    }
    };

    // DONUT CHART FOR WASTE TYPE PERCENTAGE - END

    // WASTE TYPE BY PERIOD GRAPH - START

    // Define your data interface
    interface RecordType {
    wasteTypeLabel: string;
    periodLabel: string;
    totalWeight: number;
    typeColorCode: string;
    }

    // Type your input data
    const records = homeData.getHomeData.dashboard[3];

    // Extract periods and waste types
    const periods: string[] = Array.from(
    new Set(records.map((r: RecordType) => r.periodLabel))
    ) as string[];

    const wasteTypes: string[] = Array.from(
    new Set(records.map((r: RecordType) => r.wasteTypeLabel))
    ) as string[];

    // Type the helper lookup objects
    const colorByWaste: Record<string, string> = {};
    const byTypePeriod: Record<string, Record<string, number>> = {};

    // Build color and value lookups
    records.forEach((r: RecordType) => {
    if (!colorByWaste[r.wasteTypeLabel]) colorByWaste[r.wasteTypeLabel] = r.typeColorCode;
    if (!byTypePeriod[r.wasteTypeLabel]) byTypePeriod[r.wasteTypeLabel] = {};
    byTypePeriod[r.wasteTypeLabel][r.periodLabel] = r.totalWeight;
    });

    function formatName(wasteName: string): string {
        const maxWordsPerLine = 3;
        if (!wasteName) return "";

        const words = wasteName.split(" ");
        const lines: string[] = [];
        let current: string[] = [];

        words.forEach(w => {
            current.push(w);
            if (current.length === maxWordsPerLine) {
            lines.push(current.join(" "));
            current = [];
            }
        });

        if (current.length) {
            lines.push(current.join(" "));
        }

        return lines.join("\n"); // ECharts uses '\n' as newline
    }


    // Build series for ECharts
    const series = wasteTypes.map(wt => ({
    name: formatName(wt),
    type: "bar",
    stack: "Ad",
    emphasis: { focus: "series" },
    itemStyle: { color: colorByWaste[wt], borderColor: "#ffffff", borderWidth: 1 },
    data: periods.map(period => byTypePeriod[wt]?.[period] ?? 0),
    }));

    // Transform date strings "yyyy-mm-dd" to "dd/mm"
    const periodsFormatted = periods.map(dateStr => {
    // Check if string matches the full yyyy-mm-dd format:
    // It should have exactly 3 parts separated by '-' 
    // and the third part (day) should be a number
    const parts = dateStr.split("-");
    if (
        parts.length === 3 &&
        /^\d{4}$/.test(parts[0]) &&   // year is 4 digits
        /^\d{2}$/.test(parts[1]) &&   // month is 2 digits
        /^\d{2}$/.test(parts[2])      // day is 2 digits
    ) {
        return {value: `${parts[2]}/${parts[1]}`, textStyle: { fontSize: chartCommonFontSize }}; // return dd/mm
    }

    else if (
        parts.length === 2 &&
        /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)$/.test(parts[0]) &&
        /^\d{4}$/.test(parts[1])
    ) {
        const shortYear = parts[1].slice(-2);  // last 2 digits of year
        return {value: `${parts[0]} '${shortYear}`, textStyle: { fontSize: chartCommonFontSize }}; // return mm/yyyy
    }

    // Otherwise return original string (e.g. Feb-2024, w28)
    return {value: dateStr, textStyle: { fontSize: chartCommonFontSize } };
    });

    // Build final wastetypeperiod object
    const wastetypeperiod = {
    type: "echarts@v5a",
    width: 3000,
    height: 800,
    option: {
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        legend: {             
            show: true,
            itemGap: 8,
            right: 15,
            left: "80%",
            top: "center",
            orient: "vertical",
            textStyle: { fontSize: chartCommonFontSize }
        },
        grid: {
            left: "0%",
            right: "22%",
            bottom: "5%",
            top: "5%",
            containLabel: true
        },
        xAxis: [{ type: "category", 
            data: periodsFormatted }],
        yAxis: [{ type: "value",
            axisLabel: { fontSize: chartCommonFontSize }
         }],
        series,
    },
    };
    // WASTE TYPE BY PERIOD GRAPH - END

    // WASTE TYPE BY PERIOD GRAPH - TABLE

    // Transform date strings "yyyy-mm-dd" to "dd/mm"
    const periodsFormattedTable = periods.map(dateStr => {
    // Check if string matches the full yyyy-mm-dd format:
    // It should have exactly 3 parts separated by '-' 
    // and the third part (day) should be a number
    const parts = dateStr.split("-");
    if (
        parts.length === 3 &&
        /^\d{4}$/.test(parts[0]) &&   // year is 4 digits
        /^\d{2}$/.test(parts[1]) &&   // month is 2 digits
        /^\d{2}$/.test(parts[2])      // day is 2 digits
    ) {
        return `${parts[2]}/${parts[1]}`; // return dd/mm
    }

    else if (
        parts.length === 2 &&
        /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)$/.test(parts[0]) &&
        /^\d{4}$/.test(parts[1])
    ) {
        const shortYear = parts[1].slice(-2);  // last 2 digits of year
        return `${parts[0]} '${shortYear}`; // return mm/yyyy
    }

    // Otherwise return original string (e.g. Feb-2024, w28)
    return dateStr;
    });

    // const wastetypeperiodtable = {
    // labels: [...wasteTypes],
    // values: periods.map((period, index) => ({  // Use original periods array, not periodsFormatted
    //     wastename: periodsFormattedTable[index],      // Use the formatted version for display
    //     data: [...wasteTypes.map(wt => byTypePeriod[wt]?.[period] ?? 0)] // Use original period for lookup
    // }))
    // };

    const wastetypeperiodtable = {
    // Dates as column headers (formatted)
    labels: [...periodsFormattedTable],

    // Each row is a waste type, data stays in the same "grid" position
    values: wasteTypes.map(wt => ({
        wastename: wt,
        data: periods.map(period => byTypePeriod[wt]?.[period] ?? 0)
    }))
    };

    // TOP SITES BY WASTE TYPE - START

    // Define your data interface
    interface RecordType {
    wasteTypeLabel: string;
    totalWeight: number;
    typeColorCode: string;
    customerSiteLabel: string;
    }

    // Input data
    const records2: RecordType[] = homeData.getHomeData.dashboard[4];

    // Extract sites and waste types
    const sites: string[] = Array.from(
    new Set(records2.map((r: RecordType) => r.customerSiteLabel))
    ) as string[];
    const limitedSites = sites.slice(0, 5);

    const wasteTypes2: string[] = Array.from(
    new Set(records2.map((r: RecordType) => r.wasteTypeLabel))
    ) as string[];

    // Type the helper lookup objects
    const colorByWaste2: Record<string, string> = {};
    const byTypeSite: Record<string, Record<string, number>> = {};

    // Build color and value lookups
    records2.forEach((r: RecordType) => {
    if (!colorByWaste2[r.wasteTypeLabel]) colorByWaste2[r.wasteTypeLabel] = r.typeColorCode;
    if (!byTypeSite[r.wasteTypeLabel]) byTypeSite[r.wasteTypeLabel] = {};
    byTypeSite[r.wasteTypeLabel][r.customerSiteLabel] = r.totalWeight;
    });

    // Chart (only first 5 sites)
    const limitedSeries2 = wasteTypes2.map(wt => ({
    name: wt,
    type: "bar",
    stack: "Ad",
    emphasis: { focus: "series" },
    itemStyle: { color: colorByWaste2[wt], borderColor: "#ffffff", borderWidth: 1 },
    data: limitedSites.map(site => byTypeSite[wt]?.[site] ?? 0),
    }));

    // Payload for yAxis only with adjusted font size
    const limitedSitesWithFontSize = limitedSites.map(site => ({value: site, textStyle: { fontSize: chartCommonFontSize }}));

    const topsitesbywastetype = {
    type: "echarts@v5a",
    width: 3000,
    height: 800,
    option: {
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        legend: { 
            show: true,
            itemGap: 8,
            right: "10%",
            left: "70%",
            top: "center",
            orient: "vertical",
            textStyle: { fontSize: chartCommonFontSize }
        },
        grid: { 
            left: "15%",
            right: "35%",
            bottom: "5%",
            top: "5%",
            containLabel: true
        },
        xAxis: [{ type: "value", axisLabel: { fontSize: chartCommonFontSize } }],
        yAxis: [{ type: "category", inverse: true, data: limitedSitesWithFontSize }], // Changing order shown
        series: limitedSeries2,
    },
    };

    // Table (all sites shown)
    const topsitesbywastetypetable = {
    labels: [...sites],
    values: wasteTypes2.map(wt => ({
        color: colorByWaste2[wt] || "", // Color
        wastename: wt,                      // Waste type
        data: [ ...sites.map(site => byTypeSite[wt]?.[site] ?? 0) ] // Data columns
    }))
    };

    const topsitesbywastetypetableinverted = {
    labels: [...wasteTypes2],
    values: sites.map(site => ({
        sitename: site,                      // Site name
        data: [ ...wasteTypes2.map(wt => byTypeSite[wt]?.[site] ?? 0) ] // Data columns
    }))
};
    // TOP SITES BY WASTE TYPE - END

    // TOTAL WASTE RECYCLED AND RECOVERED TABLE - START

    // Define your data interface
    interface RecordType {
    recycledOrRecovered: string;
    periodLabel: string;
    totalWeight: number;
    rorColorCode: string;
    }

    // Type your input data
    const recordsTotalWasteRecycledRecoveredTable: RecordType[] = homeData.getHomeData.dashboard[0];

    // Extract all periods
    const periodsRRTable: string[] = Array.from(
    new Set(recordsTotalWasteRecycledRecoveredTable.map(r => r.periodLabel))
    );

    // Helper to format date as DD/MM
    function formatDate(dateStr: string): string {
    const parts = dateStr.split("-");
    if (parts.length === 3 &&
        /^\d{4}$/.test(parts[0]) &&
        /^\d{2}$/.test(parts[1]) &&
        /^\d{2}$/.test(parts[2])
    ) {
        return `${parts[2]}/${parts[1]}`;
    }

    else if (
        parts.length === 2 &&
        /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)$/.test(parts[0]) &&
        /^\d{4}$/.test(parts[1])
    ) {
        const shortYear = parts[1].slice(-2);  // last 2 digits of year
        return `${parts[0]} '${shortYear}`;
    }

    return dateStr;
    }

    // Prepare mapping of "Recycled" and "Recovered" weights by period
    const byTypePeriodRRTable: Record<string, Record<string, number>> = { "Recovered": {}, "Recycled": {} };
    periodsRRTable.forEach(period => {
    byTypePeriodRRTable["Recovered"][period] = 0;
    byTypePeriodRRTable["Recycled"][period] = 0;
    });
    recordsTotalWasteRecycledRecoveredTable.forEach(r => {
    byTypePeriodRRTable[r.recycledOrRecovered][r.periodLabel] = r.totalWeight;
    });

    // Final array with separate Recycled and Recovered fields (and % recycled)
    const totalwasterrtable = periodsRRTable.map(period => {
    const recycled = byTypePeriodRRTable["Recycled"][period] || 0;
    const recovered = byTypePeriodRRTable["Recovered"][period] || 0;
    const total = recycled + recovered;
    const percentRecycled = total > 0 ? +(recycled / total * 100).toFixed(2) : 0;
    return {
        Date: formatDate(period),    // DD/MM
        Recycled: recycled,
        Recovered: recovered,
        Percentage: percentRecycled,
    };
    });

    // Example totalwasterrtable array:
    // [
    //   { Date: "01/11", Recycled: 128.42, Recovered: 72.58, Percentage: 63.89 },
    //   { Date: "08/11", Recycled: 110.22, Recovered: 89.78, Percentage: 55.11 }
    // ]

    // TOTAL WASTE RECYCLED AND RECOVERED TABLE - END


    // TOTAL WASTE RECYCLED AND RECOVERED CHART - START

    // Define your data interface
    interface RecordType {
    recycledOrRecovered: string;
    periodLabel: string;
    totalWeight: number;
    rorColorCode: string;
    }

    // Type your input data
    const recordsTotalWasteRecycledRecovered = homeData.getHomeData.dashboard[0];

    // Extract periods and waste types
    const periodsRR: string[] = Array.from(
    new Set(recordsTotalWasteRecycledRecovered.map((r: RecordType) => r.periodLabel))
    ) as string[];

    const wasteTypesRR: string[] = Array.from(
    new Set(recordsTotalWasteRecycledRecovered.map((r: RecordType) => r.recycledOrRecovered))
    ) as string[];

    // Ensure both "Recovered" and "Recycled" are always present
    const requiredTypes = ["Recovered", "Recycled"];
    const allWasteTypesRR = Array.from(new Set([...wasteTypesRR, ...requiredTypes]));

    // Sort waste types to ensure "Recovered" comes first, then "Recycled"
    const sortedWasteTypesRR = allWasteTypesRR.sort((a, b) => {
    if (a === "Recovered") return -1;
    if (b === "Recovered") return 1;
    if (a === "Recycled") return -1;
    if (b === "Recycled") return 1;
    return 0;
    });

    // Type the helper lookup objects
    const colorByWasteRR: Record<string, string> = {};
    const byTypePeriodRR: Record<string, Record<string, number>> = {};

    // Build color and value lookups
    recordsTotalWasteRecycledRecovered.forEach((r: RecordType) => {
    if (!colorByWasteRR[r.recycledOrRecovered]) colorByWasteRR[r.recycledOrRecovered] = r.rorColorCode;
    if (!byTypePeriodRR[r.recycledOrRecovered]) byTypePeriodRR[r.recycledOrRecovered] = {};
    byTypePeriodRR[r.recycledOrRecovered][r.periodLabel] = r.totalWeight;
    });

    // Ensure default colors for required types if they don't exist in data
    if (!colorByWasteRR["Recovered"]) colorByWasteRR["Recovered"] = "#1f2734"; // Default green
    if (!colorByWasteRR["Recycled"]) colorByWasteRR["Recycled"] = "#FF6347"; // Default blue

    // Ensure data structure exists for required types with zero values if missing
    requiredTypes.forEach(type => {
      if (!byTypePeriodRR[type]) {
        byTypePeriodRR[type] = {};
        periodsRR.forEach(period => {
          byTypePeriodRR[type][period] = 0;
        });
      } else {
        // Ensure all periods have values (fill missing periods with 0)
        periodsRR.forEach(period => {
          if (byTypePeriodRR[type][period] === undefined) {
            byTypePeriodRR[type][period] = 0;
          }
        });
      }
    });

    // Calculate the percentage of recycled waste per period
    const percentageRecycledData = periodsRR.map(period => {
    const total = sortedWasteTypesRR.reduce((sum, wt) => sum + (byTypePeriodRR[wt]?.[period] ?? 0), 0);
    const recycled = byTypePeriodRR["Recycled"]?.[period] ?? 0;
    return total > 0 ? +(recycled / total * 100).toFixed(2) : 0;
    });

    // Build series for ECharts using sorted waste types (Recovered first, then Recycled)
    const seriesRR = sortedWasteTypesRR.map(wt => ({
    name: wt,
    type: "bar",
    stack: "Ad",
    emphasis: { focus: "series" },
    itemStyle: { color: colorByWasteRR[wt] },
    data: periodsRR.map(period => byTypePeriodRR[wt]?.[period] ?? 0),
    datapercent: percentageRecycledData,
    }));

    // Transform date strings "yyyy-mm-dd" to "dd/mm"
    const periodsRRFormatted = periodsRR.map(dateStr => {
    // Check if string matches the full yyyy-mm-dd format:
    // It should have exactly 3 parts separated by '-' 
    // and the third part (day) should be a number
    const parts = dateStr.split("-");
    if (
        parts.length === 3 &&
        /^\d{4}$/.test(parts[0]) &&   // year is 4 digits
        /^\d{2}$/.test(parts[1]) &&   // month is 2 digits
        /^\d{2}$/.test(parts[2])      // day is 2 digits
    ) {
        return {value: `${parts[2]}/${parts[1]}`, textStyle: { fontSize: chartCommonFontSize }}; // return dd/mm
    }
    else if (
        parts.length === 2 &&
        /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)$/.test(parts[0]) &&
        /^\d{4}$/.test(parts[1])
    ) {
        const shortYear = parts[1].slice(-2);  // last 2 digits of year
        return {value: `${parts[0]} '${shortYear}`, textStyle: { fontSize: chartCommonFontSize }}; // return mm/yyyy
    }

    // Otherwise return original string (e.g. Feb-2024, w28)
    return {value: dateStr, textStyle: { fontSize: chartCommonFontSize }};
    });

    // Build final totalwasterr object
    const totalwasterr = {
    type: "echarts@v5a",
    width: 3000,
    height: 800,
    option: {
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        legend: { show: true, textStyle: { fontSize: chartCommonFontSize } },
        grid: { left: "3%", right: "4%", bottom: "3%", containLabel: true },
        xAxis: [{ type: "category", data: periodsRRFormatted }],
        yAxis: [{ type: "value", axisLabel: { fontSize: chartCommonFontSize } }],
        series: seriesRR,
    },
    };

    // TOTAL WASTE RECYCLED AND RECOVERED CHART - END

    // CO2 EMISSIONS GENERATED - START

    // Define your data interface
    interface RecordType {
    periodLabel: string;
    co2EmissionsGenerated: number;
    }

    // Type your input data
    const recordsCO = homeData.getHomeData.dashboard[1];

    // Extract periods (X-axis labels)
    const periodsCO: string[] = Array.from(
    new Set(recordsCO.map((r: RecordType) => r.periodLabel))
    ) as string[];

    // Step 1: Build the data array for one line series
    const co2DataArray = periodsCO.map(period => {
    // Find the matching record for this period
    const record = recordsCO.find((r: RecordType) => r.periodLabel === period);
    return record ? record.co2EmissionsGenerated : 0;
    });

    // Step 2: Configure the series with one line
    const seriesCO = [{
        data: co2DataArray,
        type: "line",
        smooth: true, // must be boolean
        symbol: "circle",
        symbolSize: 26,
        lineStyle: {
            width: 10,
            color: "#5abdc4"
        },
        itemStyle: {
            color: "#80dbdf"
        }
    }];

    // Transform date strings "yyyy-mm-dd" to "dd/mm"
    const periodsCOFormatted = periodsCO.map(dateStr => {
    // Check if string matches the full yyyy-mm-dd format:
    // It should have exactly 3 parts separated by '-' 
    // and the third part (day) should be a number
    const parts = dateStr.split("-");
    if (
        parts.length === 3 &&
        /^\d{4}$/.test(parts[0]) &&   // year is 4 digits
        /^\d{2}$/.test(parts[1]) &&   // month is 2 digits
        /^\d{2}$/.test(parts[2])      // day is 2 digits
    ) {
        return {value: `${parts[2]}/${parts[1]}`, textStyle: { fontSize: chartCommonFontSize }}; // return dd/mm
    }

    else if (
        parts.length === 2 &&
        /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)$/.test(parts[0]) &&
        /^\d{4}$/.test(parts[1])
    ) {
        const shortYear = parts[1].slice(-2);  // last 2 digits of year
        return {value: `${parts[0]} '${shortYear}`, textStyle: { fontSize: chartCommonFontSize }}; // return mm/yyyy
    }

    // Otherwise return original string (e.g. Feb-2024, w28)
    return {value: dateStr, textStyle: { fontSize: chartCommonFontSize }};
    });

    // Step 3: Construct the complete ECharts option
    const totalWasteCO = {
    type: "echarts@v5a",
    width: 3000,
    height: 800,
    option: {
            grid: {
                left: "2%",
                right: "2%",
                bottom: "10%",
                top: "2%",
            },
        xAxis: [{ type: "category",  data: periodsCOFormatted }],
        yAxis: [{ type: "value", axisLabel: { fontSize: chartCommonFontSize } }],
        series: seriesCO,
    },
    };

    const totalWasteCOtable = {
    // Column headers
    columns: ["Date", "CO2 Emissions Generated"],

    // Rows: [date, value]
    rows: periodsCOFormatted.map((labelObj, index) => [
        labelObj.value,          // formatted date string
        co2DataArray[index] ?? 0 // numeric CO2 value
    ])
    };

    // CO2 EMISSIONS GENERATED - END

    // TOP 5 AND REST OF SITES BY RECYCLING RATE
    const array12 = homeData.getHomeData.dashboard[12];
    const topFiveSitesByRecyclingRate = array12.slice(0, 5);
    const restSitesByRecyclingRate = array12.slice(5);

    const array13 = homeData.getHomeData.dashboard[13];
    const topFiveSitesByEmissionRate = array13.slice(0, 5);
    const restSitesByEmissionRate = array13.slice(5);

    const array14 = homeData.getHomeData.dashboard[14];
    const topFiveSitesByWasteRate = array14.slice(0, 5);
    const restSitesByWasteRate = array14.slice(5);

    // Helper function to clean site labels in the final output
    const cleanSiteLabels = (data: any): any => {
      if (Array.isArray(data)) {
        return data.map(cleanSiteLabels);
      }
      if (data && typeof data === 'object') {
        const newObj: any = {};
        for (const key in data) {
          if (key === 'customerSiteLabel' && typeof data[key] === 'string') {
            newObj[key] = data[key].replace(/&/g, "and");
          } else {
            newObj[key] = cleanSiteLabels(data[key]);
          }
        }
        return newObj;
      }
      return data;
    };

    // Clean the final values before returning
    topsitesbywastetype.option.yAxis[0].data = topsitesbywastetype.option.yAxis[0].data.map((site: any) => ({
      ...site,
      value: site.value.replace(/&/g, "and"),
    }));
    topsitesbywastetypetable.labels = topsitesbywastetypetable.labels.map((label: string) => label.replace(/&/g, "and"));
    topsitesbywastetypetableinverted.values = topsitesbywastetypetableinverted.values.map((item: any) => ({
      ...item,
      sitename: item.sitename.replace(/&/g, "and"),
    }));

    return {
        ...homeData?.getHomeData,
        period: {
        startDate: startDate?.toISOString() ?? null,
        endDate: endDate?.toISOString() ?? null,
        },
        selectedCustomer: selectedCustomer?.label ?? "",
        allSites: sitesPerCustomer ?? [],
        allWasteTypes: wasteTypesPerCustomer ?? [],
        selectedSites: {
        ids: getIds(selectedSites),
        labels: getLabels(selectedSites).map(label => label.replace(/&/g, "and")),
        },
        selectedWasteTypes: {
        ids: getIds(selectedWasteTypes),
        labels: getLabels(selectedWasteTypes).map(label => label.replace(/&/g, "and")),
        },
        wastetypepercent,
        wastetypeperiod,
        wastetypeperiodtable,
        topsitesbywastetype,
        topsitesbywastetypetable,
        topsitesbywastetypetableinverted,
        totalwasterr,
        totalwasterrtable,
        totalWasteCO,
        totalWasteCOtable,
        topFiveSitesByRecyclingRate: cleanSiteLabels(topFiveSitesByRecyclingRate),
        restSitesByRecyclingRate: cleanSiteLabels(restSitesByRecyclingRate),
        topFiveSitesByEmissionRate: cleanSiteLabels(topFiveSitesByEmissionRate),
        restSitesByEmissionRate: cleanSiteLabels(restSitesByEmissionRate),
        topFiveSitesByWasteRate: cleanSiteLabels(topFiveSitesByWasteRate),
        restSitesByWasteRate: cleanSiteLabels(restSitesByWasteRate),
    };
    };

  const homePageProps = useMemo(
    () => ({
      isLoading,
      customerList,
      isTippingUser,
      leaderboardRecycling,
      leaderboardEmissions,
      leaderboardWaste,
      sitesPerCustomer,
      wasteTypesPerCustomer,
      startDate,
      endDate,
      exportInProgress,
      achievementDataArray,
      totalWasteCount,
      recycledWasteTypes,
      recycledWasteDetails,
      colorArray,
      overallRecycledWastes,
      overallRecycledWastesColors,
      emissionsGeneratedDetails,
      filterLeaderboardValue,
      sortingLeaderboardValue,
      suggestionsArray,
      currentPage,
      supportUsername,
      supportEmail,
      eventbriteBanner,
      eventbriteTitle,
      eventbriteEventLink,
      selectedSites,
      selectedWasteTypes,
      selectedCustomer,
      printRecycledWasteTypes,
      printRecycledWasteDetails,
      WTByPeriodGraph,
      WTByPeriodTable,
      WTByPeriodLegend,
      WTBySiteGraph,
      WTBySiteTable,
      WTBySiteLegend,
      overallRecycledWastesExportTable,
      LBRecyclingRateTopGraph,
      LBRecyclingRateTopTable,
      LBEmissionRateBottomGraph,
      LBEmissionRateBottomTable,
      LBReducedWasteRateTopGraph,
      LBReducedWasteRateTopTable,
      achievementsTipping,
      levelDataTable,
      feeDataTable,
      tonnageDataTable,
      containerRef,
      sliderRef,
      setFilterLeaderboardValue,
      setSortingLeaderboardValue,
      setSelectedCustomer,
      setSelectedSites,
      setSelectedWasteTypes,
      setDateRange,
      setExportInProgress,
      setCurrentPage,
      darkMode,
      width,
      animationDuration,
    }),
    [
      isLoading,
      customerList,
      isTippingUser,
      leaderboardRecycling,
      leaderboardEmissions,
      leaderboardWaste,
      sitesPerCustomer,
      wasteTypesPerCustomer,
      startDate,
      endDate,
      exportInProgress,
      achievementDataArray,
      totalWasteCount,
      recycledWasteTypes,
      recycledWasteDetails,
      colorArray,
      overallRecycledWastes,
      overallRecycledWastesColors,
      emissionsGeneratedDetails,
      filterLeaderboardValue,
      sortingLeaderboardValue,
      suggestionsArray,
      currentPage,
      supportUsername,
      supportEmail,
      eventbriteBanner,
      eventbriteTitle,
      eventbriteEventLink,
      selectedSites,
      selectedWasteTypes,
      selectedCustomer,
      printRecycledWasteTypes,
      printRecycledWasteDetails,
      WTByPeriodGraph,
      WTByPeriodTable,
      WTByPeriodLegend,
      WTBySiteGraph,
      WTBySiteTable,
      WTBySiteLegend,
      overallRecycledWastesExportTable,
      LBRecyclingRateTopGraph,
      LBRecyclingRateTopTable,
      LBEmissionRateBottomGraph,
      LBEmissionRateBottomTable,
      LBReducedWasteRateTopGraph,
      LBReducedWasteRateTopTable,
      achievementsTipping,
      levelDataTable,
      feeDataTable,
      tonnageDataTable,
      darkMode,
      width,
    ]
  );

  return <HomePageUI {...homePageProps} getCarboneExportPayload={getCarboneExportPayload} />;
};

export default HomePage;
